import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { useLocale } from "@/contexts/LocaleContext";
import { Twitter, Facebook, Share2 } from "lucide-react";

export const useBidNotifications = () => {
  const { user } = useAuth();
  const { locale } = useLocale();

  const shareOnTwitter = (eventTitle: string, won: boolean) => {
    const text = won
      ? locale.language === "pt"
        ? `🎉 Acabei de ganhar minha previsão em "${eventTitle}" no Callyout! 🚀`
        : `🎉 Just won my prediction on "${eventTitle}" at Callyout! 🚀`
      : "";
    const url = window.location.origin;
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      "_blank"
    );
  };

  const shareOnFacebook = () => {
    const url = window.location.origin;
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      "_blank"
    );
  };

  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel("bid-notifications")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "events",
        },
        async (payload) => {
          const event = payload.new as {
            id: string;
            title: string;
            title_pt: string | null;
            outcome: boolean | null;
            status: string;
          };

          // Only process resolved events
          if (event.status !== "RESOLVED" || event.outcome === null) return;

          // Check if user had a bid on this event
          const { data: userBid } = await supabase
            .from("bids")
            .select("prediction, amount")
            .eq("event_id", event.id)
            .eq("user_id", user.id)
            .maybeSingle();

          if (userBid) {
            const won = userBid.prediction === event.outcome;
            const eventTitle =
              locale.language === "pt" && event.title_pt
                ? event.title_pt
                : event.title;

            if (won) {
              toast.success(
                locale.language === "pt"
                  ? `🎉 Você ganhou sua previsão em "${eventTitle}"!`
                  : `🎉 You won your prediction on "${eventTitle}"!`,
                {
                  description: (
                    <div className="mt-2">
                      <p className="text-sm mb-2">
                        {locale.language === "pt"
                          ? "Compartilhe sua vitória!"
                          : "Share your win!"}
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => shareOnTwitter(eventTitle, true)}
                          className="p-2 rounded-lg bg-[#1DA1F2]/20 hover:bg-[#1DA1F2]/30 transition-colors"
                        >
                          <Twitter size={16} className="text-[#1DA1F2]" />
                        </button>
                        <button
                          onClick={shareOnFacebook}
                          className="p-2 rounded-lg bg-[#4267B2]/20 hover:bg-[#4267B2]/30 transition-colors"
                        >
                          <Facebook size={16} className="text-[#4267B2]" />
                        </button>
                      </div>
                    </div>
                  ),
                  duration: 15000,
                }
              );
            } else {
              toast.error(
                locale.language === "pt"
                  ? `😔 Você perdeu sua previsão em "${eventTitle}"`
                  : `😔 You lost your prediction on "${eventTitle}"`,
                {
                  description:
                    locale.language === "pt"
                      ? `O resultado foi ${event.outcome ? "SIM" : "NÃO"}`
                      : `The outcome was ${event.outcome ? "YES" : "NO"}`,
                  duration: 8000,
                }
              );
            }
          }
        }
      )
      .subscribe((status, err) => {
        // Silently handle connection errors to prevent console noise
        // Realtime notifications are a nice-to-have, not critical functionality
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          // Connection failed - users will still see results when refreshing
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, locale.language]);
};
